import type { OFFER_ACCEPTANCE_STATUS } from './offer';
import type { ValueOf } from './util/typeUtils';

export const EUROPACE_SHOPWINDOW_CONDITION_USAGES = {
    FAHRZEUGKAUF: 'FAHRZEUGKAUF',
    FREIE_VERWENDUNG: 'FREIE_VERWENDUNG',
    MODERNISIEREN: 'MODERNISIEREN',
    UMSCHULDUNG: 'UMSCHULDUNG',
} as const;
export type EUROPACE_SHOPWINDOW_CONDITION_USAGE = keyof typeof EUROPACE_SHOPWINDOW_CONDITION_USAGES;

export const EUROPACE_PAYMENT_DATES = {
    MONATSENDE: 'MONATSENDE',
    MONATSMITTE: 'MONATSMITTE',
} as const;
export type EUROPACE_PAYMENT_DATE = keyof typeof EUROPACE_PAYMENT_DATES;

export const EUROPACE_CAR_VENDORS = {
    HAENDLER: 'HAENDLER',
    PRIVAT: 'PRIVAT',
} as const;

export type EUROPACE_CAR_VENDOR = keyof typeof EUROPACE_CAR_VENDORS;

export const EUROPACE_FEASIBILITY_STATUS = {
    MACHBAR: 'MACHBAR',
    MACHBAR_UNTER_VORBEHALT: 'MACHBAR_UNTER_VORBEHALT',
    NICHT_MACHBAR: 'NICHT_MACHBAR',
} as const;
export type EUROPACE_TYPE_FEASIBILITY_STATUS = keyof typeof EUROPACE_FEASIBILITY_STATUS;

export const EUROPACE_INSURED_RISKS = {
    ARBEITSLOSIGKEIT: 'ARBEITSLOSIGKEIT',
    ARBEITSUNFAEHIGKEIT: 'ARBEITSUNFAEHIGKEIT',
    LEBEN: 'LEBEN',
} as const;
export type EUROPACE_INSURED_RISK = keyof typeof EUROPACE_INSURED_RISKS;

export const EUROPACE_COMPLETENESS_CATEGORIES = {
    AUSGABEN: 'AUSGABEN',
    BESCHAEFTIGUNG: 'BESCHAEFTIGUNG',
    EINNAHMEN: 'EINNAHMEN',
    FINANZBEDARF: 'FINANZBEDARF',
    HERKUNFT: 'HERKUNFT',
    IMMOBILIEN: 'IMMOBILIEN',
    KINDER: 'KINDER',
    PERSONENDATEN: 'PERSONENDATEN',
    SOFORTKREDIT: 'SOFORTKREDIT',
    VERBINDLICHKEITEN: 'VERBINDLICHKEITEN',
    VERMITTLER: 'VERMITTLER',
    VORGANG: 'VORGANG',
    WOHNSITUATION: 'WOHNSITUATION',
} as const;
export type EUROPACE_COMPLETENESS_CATEGORY = keyof typeof EUROPACE_COMPLETENESS_CATEGORIES;

export const EUROPACE_COMPLETENESS_REASONS = {
    VALUE_IS_EMPTY: 'VALUE_IS_EMPTY',
    VALUE_IS_INVALID: 'VALUE_IS_INVALID',
    VALUE_OUT_OF_RANGE: 'VALUE_OUT_OF_RANGE',
} as const;
export type EUROPACE_COMPLETENESS_REASON = keyof typeof EUROPACE_COMPLETENESS_REASONS;

export const EUROPACE_COMPLETENESS_IDENTIFIER_TYPES = {
    ANTRAGSTELLER: 'ANTRAGSTELLER',
    VERMITTLER: 'VERMITTLER',
} as const;
export type EUROPACE_COMPLETENESS_IDENTIFIER_TYPE = keyof typeof EUROPACE_COMPLETENESS_IDENTIFIER_TYPES;

export const EUROPACE_INCOMPLETENESS_MESSAGES_IDENTIFIER_TYPES = {
    ANTRAGSTELLER_1: 'ANTRAGSTELLER_1',
    ANTRAGSTELLER_2: 'ANTRAGSTELLER_2',
    ANTRAGSTELLER_BOTH: 'ANTRAGSTELLER_BOTH',
    VERMITTLER: 'VERMITTLER',
} as const;
export type EUROPACE_INCOMPLETENESS_MESSAGES_IDENTIFIER_TYPE =
    keyof typeof EUROPACE_INCOMPLETENESS_MESSAGES_IDENTIFIER_TYPES;

export const EUROPACE_COMPLETENESS_STATUS = {
    UNVOLLSTAENDIG: 'UNVOLLSTAENDIG',
    VOLLSTAENDIG: 'VOLLSTAENDIG',
} as const;
export type EUROPACE_TYPE_COMPLETENESS_STATUS = keyof typeof EUROPACE_COMPLETENESS_STATUS;

export type EUROPACE_OFFER_ACCEPTANCE_STATUS = Extract<OFFER_ACCEPTANCE_STATUS, 'SUCCESS' | 'FAILURE' | 'PENDING'>;

export const EUROPACE_PROCESS_STATUSES = {
    FAILED: 'FAILED',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;

export type EUROPACE_PROCESS_STATUS = keyof typeof EUROPACE_PROCESS_STATUSES;

export const EUROPACE_PROCESS_PLATFORMS = {
    EUROPACE2: 'EUROPACE2',
    STARPOOL: 'STARPOOL',
} as const;
export type EUROPACE_PROCESS_PLATFORM = keyof typeof EUROPACE_PROCESS_PLATFORMS;

export const EUROPACE_UNTERLAGENAKTE_URL = 'https://www.europace2.de/unterlagenakte/index.html';

export const EUROPACE_GRAPHQL_FAILED_REASONS = {
    // Request format is invalid (mandatory fields are missing, wrong parameter names or values, …)
    BAD_REQUEST: 'BAD_REQUEST',
    CONFLICT: 'CONFLICT',
    // The authenticated user does not have sufficient rights
    FORBIDDEN: 'FORBIDDEN',
    // The Vorgang was deleted in the meantime
    GONE: 'GONE',
    // The Vorgang does not exist
    PROCESS_NOT_FOUND: 'PROCESS_NOT_FOUND',
    PROCESSING_FAILED: 'PROCESSING_FAILED',
    UNPROCESSABLE: 'UNPROCESSABLE',
} as const;

export type EUROPACE_GRAPHQL_FAILED_REASON = keyof typeof EUROPACE_GRAPHQL_FAILED_REASONS;

export const EUROPACE_UPDATE_FAILED_REASONS = {
    ...EUROPACE_GRAPHQL_FAILED_REASONS,
    EMPTY_QUERY: 'EMPTY_QUERY',
    MAPPING_ERROR: 'MAPPING_ERROR',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR',
    UPDATE_APPLICANT_ERROR: 'UPDATE_APPLICANT_ERROR',
    UPDATE_NO_CHANGES: 'UPDATE_NO_CHANGES',
    UPDATE_REFINANCING_MISMATCH_ERROR: 'UPDATE_REFINANCING_MISMATCH_ERROR',
} as const;

export type EUROPACE_UPDATE_FAILED_REASON = keyof typeof EUROPACE_UPDATE_FAILED_REASONS;

export const EUROPACE_ERROR_RESPONSE_MESSAGES = {
    PARTNER_ID_MISSING: 'PARTNER_ID_MISSING',
    PARTNER_ID_STARPOOL_MISSING: 'PARTNER_ID_STARPOOL_MISSING',
    PROCESS_ALREADY_CREATED: 'PROCESS_ALREADY_CREATED',
    PROCESS_NOT_IMPORTED: 'PROCESS_NOT_IMPORTED',
    PROCESS_STILL_PENDING: 'PROCESS_STILL_PENDING',
};

export type EUROPACE_ERROR_RESPONSE_MESSAGE = keyof typeof EUROPACE_ERROR_RESPONSE_MESSAGES;

export const EUROPACE_APPLICANT_STATUSES = {
    BEANTRAGT: 'BEANTRAGT',
    NICHT_ANGENOMMEN: 'NICHT_ANGENOMMEN',
    UNTERSCHRIEBEN: 'UNTERSCHRIEBEN',
    WIDERRUFEN: 'WIDERRUFEN',
} as const;

export type EUROPACE_APPLICANT_STATUS = ValueOf<typeof EUROPACE_APPLICANT_STATUSES>;

export const EUROPACE_BANK_STATUSES = {
    ABGELEHNT: 'ABGELEHNT',
    AUTOMATISCH_ABGELEHNT: 'AUTOMATISCH_ABGELEHNT',
    NICHT_BEARBEITET: 'NICHT_BEARBEITET',
    UNTERSCHRIEBEN: 'UNTERSCHRIEBEN',
    ZURUECKGESTELLT: 'ZURUECKGESTELLT',
} as const;

export type EUROPACE_BANK_STATUS = ValueOf<typeof EUROPACE_BANK_STATUSES>;

export const EUROPACE_COMMISSION_STATUSES = {
    VOLLSTAENDIG_AUSGEZAHLT: 'VOLLSTAENDIG_AUSGEZAHLT',
} as const;

export type EUROPACE_COMMISSION_STATUS = ValueOf<typeof EUROPACE_COMMISSION_STATUSES>;

export const EUROPACE_PROCESS_SAFEKEEPING_REASONS = {
    BERATERHAFTUNG: 'BERATERHAFTUNG',
    GESCHAEFTSNACHWEIS: 'GESCHAEFTSNACHWEIS',
    LEAD: 'LEAD',
} as const;

export type EUROPACE_PROCESS_SAFEKEEPING_REASON = ValueOf<typeof EUROPACE_PROCESS_SAFEKEEPING_REASONS>;

export const EUROPACE_PROCESS_DATA_CONTEXTS = {
    ECHTGESCHAEFT: 'ECHTGESCHAEFT',
    TESTUMGEBUNG: 'TESTUMGEBUNG',
} as const;

export type EUROPACE_PROCESS_DATA_CONTEXT = ValueOf<typeof EUROPACE_PROCESS_DATA_CONTEXTS>;

export const EUROPACE_PROCESS_PRIORITIES = {
    HOCH: 'HOCH',
    NEUTRAL: 'NEUTRAL',
    NIEDRIG: 'NIEDRIG',
} as const;

export type EUROPACE_PROCESS_PRIORITY = ValueOf<typeof EUROPACE_PROCESS_PRIORITIES>;

export const EUROPACE_PROCESS_APPLICATION_STATUSES = {
    AKTIV: 'AKTIV',
    ARCHIVIERT: 'ARCHIVIERT',
} as const;

export type EUROPACE_PROCESS_APPLICATION_STATUS = ValueOf<typeof EUROPACE_PROCESS_APPLICATION_STATUSES>;

export const EUROPACE_APPLICATIONS_REQUEST_INTERVALS = {
    DAILY: 'DAILY',
    HOURLY: 'HOURLY',
    NONE: 'NONE',
} as const;

export type EUROPACE_APPLICATIONS_REQUEST_INTERVAL = ValueOf<typeof EUROPACE_APPLICATIONS_REQUEST_INTERVALS>;

export const EUROPACE_PROCESS_EVENTS_REQUEST_TYPES = {
    INTERVAL: 'INTERVAL',
    MANUAL: 'MANUAL',
} as const;
export type EUROPACE_PROCESS_EVENTS_REQUEST_TYPE = ValueOf<typeof EUROPACE_PROCESS_EVENTS_REQUEST_TYPES>;

export const EUROPACE_PROCESS_EVENTS_REQUEST_STATUSES = {
    FAILED: 'FAILED',
    NOT_REQUESTED: 'NOT_REQUESTED',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;
export type EUROPACE_PROCESS_EVENTS_REQUEST_STATUS = ValueOf<typeof EUROPACE_PROCESS_EVENTS_REQUEST_STATUSES>;

export const EUROPACE_IMPORT_PROCESS_ERRORS = {
    FORBIDDEN_ERROR: 'EUROPACE_IMPORT_PROCESS_FORBIDDEN_ERROR',
    REFINANCING_MISMATCH_ERROR: 'EUROPACE_IMPORT_PROCESS_REFINANCING_MISMATCH_ERROR',
    UNAUTHORIZED_ERROR: 'EUROPACE_IMPORT_PROCESS_UNAUTHORIZED_ERROR',
    UNEXPECTED_ERROR: 'EUROPACE_IMPORT_PROCESS_UNEXPECTED_ERROR',
    UNPROCESSABLE_ENTITY_ERROR: 'EUROPACE_IMPORT_PROCESS_UNPROCESSABLE_ENTITY_ERROR',
} as const;

export type EUROPACE_IMPORT_PROCESS_ERROR = keyof typeof EUROPACE_IMPORT_PROCESS_ERRORS;

export const EUROPACE_OFFER_REQUEST_STATUSES = {
    FAILED: 'FAILED',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
};
export type EUROPACE_OFFER_REQUEST_STATUS = ValueOf<typeof EUROPACE_OFFER_REQUEST_STATUSES>;

export const EUROPACE_LEAD_SOURCES = {
    EUROPACE2: 'VERMITTLERFRONTEND_RYY49_CRM',
    STARPOOL: 'VERMITTLERFRONTEND_CYM46_CRM',
} as const;
export type EUROPACE_LEAD_SOURCE = ValueOf<typeof EUROPACE_LEAD_SOURCES>;

export const EUROPACE_OFFERS_SYNCHRONIZATION_STATUSES = {
    FAILED: 'FAILED',
    FINISHED: 'FINISHED',
    NOT_STARTED: 'NOT_STARTED',
    STARTED: 'STARTED',
} as const;
export type EUROPACE_OFFERS_SYNCHRONIZATION_STATUS = ValueOf<typeof EUROPACE_OFFERS_SYNCHRONIZATION_STATUSES>;

export const EUROPACE_PROCESS_LINK_CATEGORIES = {
    EXTERNE_FALLBEARBEITUNG: 'EXTERNE_FALLBEARBEITUNG',
} as const;

export const EUROPACE_PORTAL_LINK_KINDS = {
    AVAILABLE: 'AVAILABLE',
    EXPIRED: 'EXPIRED',
    UNAVAILABLE: 'UNAVAILABLE',
} as const;
export type EUROPACE_PORTAL_LINK_KIND = ValueOf<typeof EUROPACE_PORTAL_LINK_KINDS>;
